import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Shield, Award, Mail, MapPin } from 'lucide-react';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {onBack && (
            <button 
              onClick={onBack}
              className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Back to App</span>
            </button>
          )}
          
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="text-lg text-gray-400">Last updated: January 2025</p>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 space-y-8">
            
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We collect information to provide better services to our users. The types of information we collect include:
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-2">Information You Provide</h3>
                  <ul className="text-gray-300 space-y-1 ml-4">
                    <li>• Account registration information (email, name)</li>
                    <li>• Documents and content you upload to the App</li>
                    <li>• App preferences and settings</li>
                    <li>• Support communications and feedback</li>
                  </ul>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-2">Information We Collect Automatically</h3>
                  <ul className="text-gray-300 space-y-1 ml-4">
                    <li>• Device information (model, operating system, unique identifiers)</li>
                    <li>• App usage data and analytics</li>
                    <li>• Crash reports and performance data</li>
                    <li>• IP address and general location information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="text-gray-300 space-y-2 ml-6">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Provide and maintain our AI-powered services</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Process documents and generate podcast content</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Improve and optimize our App performance</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Provide customer support and respond to inquiries</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Send important App updates and notifications</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Prevent fraud and ensure App security</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Data Processing and AI</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Our App uses artificial intelligence to analyze your documents and generate podcast content. Here's how we handle your data:
              </p>
              <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
                <ul className="text-gray-300 space-y-2">
                  <li>• Documents are processed securely using encrypted connections</li>
                  <li>• AI processing occurs on secure servers with strict access controls</li>
                  <li>• Your original documents are not permanently stored unless you choose to save them</li>
                  <li>• Generated podcast content belongs to you and can be deleted anytime</li>
                  <li>• We do not use your content to train our AI models without explicit consent</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Information Sharing</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information. We may share information only in these limited circumstances:
              </p>
              <ul className="text-gray-300 space-y-2 ml-6">
                <li>• With your explicit consent</li>
                <li>• With trusted service providers who assist in App operations (under strict confidentiality agreements)</li>
                <li>• When required by law or to protect our legal rights</li>
                <li>• In case of business transfer (users will be notified)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We implement comprehensive security measures to protect your information:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-white font-semibold mb-2 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-green-400" />
                    Technical Safeguards
                  </h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• End-to-end encryption</li>
                    <li>• Secure data transmission</li>
                    <li>• Regular security audits</li>
                    <li>• Access controls and monitoring</li>
                  </ul>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-white font-semibold mb-2 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-blue-400" />
                    Compliance
                  </h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• GDPR compliance</li>
                    <li>• CCPA compliance</li>
                    <li>• SOC 2 Type II certified</li>
                    <li>• ISO 27001 standards</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You have control over your personal information. Your rights include:
              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Access and Download</h4>
                    <p className="text-gray-400 text-sm">Request a copy of your personal data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Correction and Update</h4>
                    <p className="text-gray-400 text-sm">Correct inaccurate or incomplete information</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Deletion</h4>
                    <p className="text-gray-400 text-sm">Request deletion of your personal data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Opt-out</h4>
                    <p className="text-gray-400 text-sm">Unsubscribe from marketing communications</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our App is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. International Data Transfers</h2>
              <p className="text-gray-300 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Changes to Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes through the App or via email. Please review this policy periodically for updates.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-700 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-white font-semibold mb-2 flex items-center">
                      <Mail className="w-5 h-5 mr-2 text-blue-400" />
                      Email
                    </h4>
                    <p className="text-gray-300">privacy@commitly.com</p>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2 flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-green-400" />
                      Address
                    </h4>
                    <p className="text-gray-300">123 Tech Street<br />San Francisco, CA 94105</p>
                  </div>
                </div>
              </div>
            </section>

          </div>

          <div className="text-center mt-12">
            <button 
              onClick={onBack}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center space-x-2 mx-auto"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
              <span>Back to Home</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;